import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import AppHeader from "@/components/AppHeader";
import { User, Save, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/utils/authFetch";

const API = process.env.REACT_APP_BACKEND_URL;

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[#71717A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</label>
);
const Input = ({ testid, ...props }) => <input data-testid={testid} className="brutalist-input" {...props} />;

export default function ContractorProfilePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    contractor_trading_title: "",
    contractor_email: "",
    contractor_phone: "",
    contractor_web: "",
    contractor_reg_no: "",
    contractor_branch_no: "",
    contractor_address: "",
    contractor_postcode: "",
  });

  useEffect(() => {
    authFetch(`${API}/api/user/contractor-details`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : {})
      .then((data) => {
        if (data && Object.keys(data).length) {
          setForm((prev) => ({ ...prev, ...data }));
        } else {
          setForm((prev) => ({
            ...prev,
            contractor_trading_title: user?.company || "",
            contractor_email: user?.email || "",
          }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/api/user/contractor-details`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed to save");
      setSaved(true);
      toast.success("Contractor details saved");
    } catch {
      toast.error("Failed to save contractor details");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <AppHeader />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#D4A537] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="contractor-profile-page">
      <AppHeader />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-2">
          <User className="w-6 h-6 text-[#D4A537]" />
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Contractor Profile
          </h1>
        </div>
        <p className="text-sm text-[#71717A] mb-8">
          These details are auto-filled when you create new certificates
        </p>

        <div className="form-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Trading title / Company name</Label>
              <Input testid="profile-trading-title" value={form.contractor_trading_title} onChange={set("contractor_trading_title")} placeholder="e.g. Self Certify UK" />
            </div>
            <div>
              <Label>Email</Label>
              <Input testid="profile-email" type="email" value={form.contractor_email} onChange={set("contractor_email")} />
            </div>
            <div>
              <Label>Telephone</Label>
              <Input testid="profile-phone" value={form.contractor_phone} onChange={set("contractor_phone")} />
            </div>
            <div className="md:col-span-2">
              <Label>Website</Label>
              <Input testid="profile-web" value={form.contractor_web} onChange={set("contractor_web")} placeholder="www.example.co.uk" />
            </div>
            <div>
              <Label>Registration No.</Label>
              <Input testid="profile-reg-no" value={form.contractor_reg_no} onChange={set("contractor_reg_no")} placeholder="N/A" />
            </div>
            <div>
              <Label>Branch No.</Label>
              <Input testid="profile-branch-no" value={form.contractor_branch_no} onChange={set("contractor_branch_no")} placeholder="N/A" />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Input testid="profile-address" value={form.contractor_address} onChange={set("contractor_address")} />
            </div>
            <div>
              <Label>Postcode</Label>
              <Input testid="profile-postcode" value={form.contractor_postcode} onChange={set("contractor_postcode")} />
            </div>
          </div>

          <div className="flex items-center gap-4 mt-8 pt-6 border-t-2 border-[#E4E4E7]">
            <button
              data-testid="profile-save-btn"
              onClick={handleSave}
              disabled={saving}
              className="brutalist-btn flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving..." : saved ? "Saved" : "Save Details"}
            </button>
            {saved && (
              <span className="text-sm text-emerald-600 font-medium">
                Details will be auto-filled on your next certificate
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
